"use client";

import { memo, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import {
  calculateStandardAddonCost,
  calculateStandardTranslationCost,
  CERTIFIED_ORDER_DELIVERY_OPTIONS,
  DELIVERY_OPTIONS_CERTIFIED,
  STANDARD_ORDER_DELIVERY_OPTIONS,
  STANDARD_TRANSLATION_MIN_USD,
  TRANSLATION_RATES,
} from "@/components/screen/start-order/components/Checkout/constants";
import { formatUSD } from "@/utils";
import { useStartOrderConfirmedQueryData } from "@/hooks/useStartOrderConfirmedQueryData";

// Slimmer order summary used by the post-purchase preview layouts.
// Same data + math as ConfirmedOrderCardContent (we share the helpers
// directly so the displayed total can never drift from what the
// customer was actually charged) but renders in a tighter card so
// the account-creation block can take the visual hierarchy.
//
// `variant="compact"`  — used by Layout 2. Shows translation row,
//                        add-ons, total, delivery date. Always open.
// `variant="collapsed"` — used by Layout 3. Shows a single line
//                         (translation type · order total) by
//                         default, expands to the compact view on
//                         click. Optimised for keeping the account
//                         block as the page's primary visual.

export type CompactOrderSummaryProps = {
  variant?: "compact" | "collapsed";
} & React.ComponentProps<"div">;

const formattedDate = new Date().toISOString().slice(0, 10);

const CompactOrderSummary = ({
  className,
  variant = "compact",
}: CompactOrderSummaryProps) => {
  const {
    pageCount,
    wordCount,
    documents,
    translationType,
    fromLanguage,
    toLanguage,
    delivery,
    isCertified,
  } = useStartOrderConfirmedQueryData();

  const [isOpen, setIsOpen] = useState(variant === "compact");

  const translationTitle = isCertified
    ? "Certified Translation"
    : "Standard Translation";

  const translationUnit = isCertified ? "page" : "word";
  const translationRateLabel = isCertified
    ? `${formatUSD(TRANSLATION_RATES.certified)} per page`
    : `${formatUSD(TRANSLATION_RATES.standard)} per word`;

  const translationNote = isCertified
    ? "250 max words per page"
    : `${formatUSD(STANDARD_TRANSLATION_MIN_USD)} minimum (1 page = 250 words)`;

  const { translationCost, unitsLabel } = useMemo(() => {
    if (
      typeof pageCount !== "number" ||
      typeof wordCount !== "number" ||
      !documents
    ) {
      return { translationCost: undefined, unitsLabel: undefined };
    }
    const translationCost = isCertified
      ? TRANSLATION_RATES.certified * pageCount
      : calculateStandardTranslationCost(wordCount);
    const unitsLabel = `${Number(isCertified ? pageCount : wordCount)} ${
      isCertified ? "Pages" : "Words"
    }`;
    return { translationCost, unitsLabel };
  }, [pageCount, wordCount, documents, isCertified]);

  const selectedDeliveryOptions = useMemo(() => {
    const flat = delivery?.flat() ?? [];
    const source = isCertified
      ? CERTIFIED_ORDER_DELIVERY_OPTIONS
      : STANDARD_ORDER_DELIVERY_OPTIONS;
    return source.filter(({ value }) => flat.includes(value));
  }, [delivery, isCertified]);

  const deliveryCost = useMemo(() => {
    return selectedDeliveryOptions.reduce((total, option) => {
      if (
        isCertified &&
        option.value === DELIVERY_OPTIONS_CERTIFIED.expeditedTurnaround
      ) {
        return total + (option.rate ?? 0) * Number(pageCount ?? 0);
      }
      if (!isCertified) {
        return (
          total +
          calculateStandardAddonCost(option.rate ?? 0, Number(wordCount ?? 0))
        );
      }
      return total + (option.rate ?? 0);
    }, 0);
  }, [selectedDeliveryOptions, isCertified, pageCount, wordCount]);

  const orderTotal = Number(translationCost ?? 0) + Number(deliveryCost ?? 0);

  // Collapsed view: a single tappable row showing translation type +
  // total, with chevron. Expands in-place into the compact body so
  // the customer never loses their place in the layout.
  if (variant === "collapsed" && !isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={cn(
          "w-full rounded-[18px] border border-border-default bg-white px-4 py-3 md:px-5 md:py-4 text-left transition-colors hover:bg-background-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          className
        )}
        aria-expanded="false"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="grid gap-0.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wide text-semantic-text-secondary">
              Order details
            </div>
            <div className="text-[14px] font-semibold text-semantic-text-primary truncate">
              {translationTitle} · {fromLanguage || "—"} →{" "}
              {toLanguage || "—"}
            </div>
            {unitsLabel && (
              <div className="text-[12px] text-semantic-text-secondary truncate">
                {unitsLabel} · Delivery {formattedDate}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="text-[16px] font-bold text-semantic-text-primary">
              {formatUSD(orderTotal)}
            </span>
            <ChevronDown
              size={18}
              className="text-semantic-text-secondary"
              aria-hidden
            />
          </div>
        </div>
      </button>
    );
  }

  return (
    <div
      className={cn(
        "rounded-[18px] border border-border-default bg-white p-4 md:p-5 text-semantic-text-primary",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <Typography
          variant="captionLg"
          className="uppercase font-bold tracking-wide text-semantic-text-secondary"
          asChild
        >
          <h3>Order details</h3>
        </Typography>
        {variant === "collapsed" && (
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-semantic-text-secondary hover:text-semantic-text-primary"
            aria-expanded="true"
          >
            Hide
            <ChevronUp size={14} aria-hidden />
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-3">
        <div className="grid gap-0.5">
          <div className="text-[14px] font-bold">{translationTitle}</div>
          <div className="text-[13px] text-semantic-text-secondary">
            Charged: {translationRateLabel}
          </div>
          <div className="text-[12px] text-semantic-text-secondary">
            ({translationNote})
          </div>
        </div>

        {typeof translationCost === "number" && (
          <div className="grid gap-1 border-t border-light-gray pt-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[14px] font-semibold">
                {fromLanguage || "—"} to {toLanguage || "—"}
              </div>
              <div className="text-[14px] font-semibold">
                {formatUSD(translationCost)}
              </div>
            </div>
            {unitsLabel && (
              <div className="text-[12px] text-semantic-text-secondary">
                {unitsLabel} · {Number(documents)}{" "}
                {Number(documents) === 1 ? "document" : "documents"}
              </div>
            )}
          </div>
        )}

        {selectedDeliveryOptions.length > 0 && (
          <div className="grid gap-1 border-t border-light-gray pt-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[13px] font-bold">Add-ons</div>
              <div className="text-[13px] font-bold">
                {formatUSD(deliveryCost)}
              </div>
            </div>
            {selectedDeliveryOptions.map(({ label, value }, i) => {
              const isExpeditedCert =
                isCertified &&
                value === DELIVERY_OPTIONS_CERTIFIED.expeditedTurnaround;
              const optionCost = isExpeditedCert
                ? (selectedDeliveryOptions[i].rate ?? 0) * Number(pageCount ?? 0)
                : isCertified
                  ? (selectedDeliveryOptions[i].rate ?? 0)
                  : calculateStandardAddonCost(
                      selectedDeliveryOptions[i].rate ?? 0,
                      Number(wordCount ?? 0)
                    );
              return (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 text-[12px] text-semantic-text-secondary"
                >
                  <span>{label}</span>
                  <span>{formatUSD(optionCost)}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-light-gray pt-3">
          <div className="text-[16px] font-bold">Order Total</div>
          <div className="text-[16px] font-bold">{formatUSD(orderTotal)}</div>
        </div>

        <div className="grid gap-0.5 border-t border-light-gray pt-3">
          <div className="text-[12px] font-bold uppercase tracking-wide text-semantic-text-secondary">
            Delivery Date
          </div>
          <div className="text-[14px] font-semibold">
            Receive your translations by:{" "}
            <span className="font-bold">{formattedDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(CompactOrderSummary);
